/**
 * Promotion Validation Rules Module
 *
 * Implements business rules for:
 * - Multi-promotion selection (Trade-Up + ONE other promotion)
 * - Configuration-driven validation for easy extension
 */

import type {
	PromotionType,
	PromotionCategory,
	PackageType,
	PromotionConfig,
	PromotionValidationResult,
	Promotion,
} from './types';

// ============================================================================
// PROMOTION CONFIGURATIONS
// ============================================================================

/**
 * Maps server promotion IDs to our internal PromotionType
 * The server may send IDs with different formatting (e.g., with hyphens)
 */
export const PROMOTION_ID_MAPPING: Record<string, PromotionType> = {
	// Trade-Up promotions - server uses "trade-up-offer-..." pattern
	'trade-up-offer-complete-system': 'tradeup-complete-system',
	'trade-up-offer-pro-2': 'tradeup-pro2',
	'trade-up-offer-pro2': 'tradeup-pro2',
	'trade-up-offer-pro-2-for-existing-pro-95-55-s-printers': 'tradeup-pro2',
	'tradeup-complete-system': 'tradeup-complete-system',
	'tradeup-pro2': 'tradeup-pro2',

	// Other promotions - various server formats
	'sprintray-pro-2-and-midas-complete-pack': 'complete-bundle',
	'sprintRay-pro-2-complete-system': 'pro2-system',
	'pro2-complete-system-free-arch-kit': 'pro2-system',
	'pro-2-complete-system-free-arch-kit': 'pro2-system',
	'upgrade-offer-pro-2-for-midas-customers': 'upgrade-offer-midas',
	'update-offer-midas-for-pro-2-95-55-s-customers': 'update-offer-midas-pro',
	'pro2-system': 'pro2-system',
	'complete-bundle': 'complete-bundle',
	'midas-offer': 'midas-offer',
	'sprintray-midas-offer': 'midas-offer',
	'upgrade-offer-midas': 'upgrade-offer-midas',
	'update-offer-midas-pro': 'update-offer-midas-pro',
};

/**
 * Check if a promotion has package mode enabled
 * This checks both the promotion's packageMode property and our config
 */
export const promotionHasPackageMode = (promotion: Promotion): boolean => {
	// If packageMode is explicitly set to 'budget' or 'none', packages are disabled
	if (promotion.packageMode === 'budget' || promotion.packageMode === 'none') {
		return false;
	}

	// Check if the promotion has packageMode explicitly set to packages or both
	if (promotion.packageMode === 'packages' || promotion.packageMode === 'both') {
		return true;
	}

	// Otherwise, check if this is a known promotion type that should have packages
	// (for server-sent promotions that might not have packageMode set)
	const normalizedId = normalizePromotionId(promotion.id);
	const knownPromosWithPackages: PromotionType[] = [
		'pro2-system',
		'midas-offer',
		'complete-bundle',
		'upgrade-offer-midas',
		'update-offer-midas-pro',
	];

	return knownPromosWithPackages.includes(normalizedId);
};

/**
 * Get the normalized promotion ID for a promotion
 */
export const normalizePromotionId = (promotionId: string): PromotionType => {
	return PROMOTION_ID_MAPPING[promotionId] || promotionId as PromotionType;
};

/**
 * Configuration for each promotion defining its category
 */
export const PROMOTION_CONFIGS: Record<PromotionType, PromotionConfig> = {
	// OTHER PROMOTIONS
	'pro2-system': {
		id: 'pro2-system',
		category: 'OTHER',
		allowedPackageTypes: ['resin'],
	},
	'upgrade-offer-midas': {
		id: 'upgrade-offer-midas',
		category: 'OTHER',
		allowedPackageTypes: ['resin'],
	},
	'midas-offer': {
		id: 'midas-offer',
		category: 'OTHER',
		allowedPackageTypes: ['addon'],
	},
	'update-offer-midas-pro': {
		id: 'update-offer-midas-pro',
		category: 'OTHER',
		allowedPackageTypes: ['addon'],
	},
	'complete-bundle': {
		id: 'complete-bundle',
		category: 'OTHER',
		allowedPackageTypes: ['resin', 'addon'],
	},

	// TRADE-UP PROMOTIONS
	'tradeup-pro2': {
		id: 'tradeup-pro2',
		category: 'TRADE_UP',
		allowedPackageTypes: [],
	},
	'tradeup-complete-system': {
		id: 'tradeup-complete-system',
		category: 'TRADE_UP',
		allowedPackageTypes: [],
	},
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get the promotion configuration for a given promotion ID
 */
export const getPromotionConfig = (promotionId: string): PromotionConfig | null => {
	const normalizedId = normalizePromotionId(promotionId);
	return PROMOTION_CONFIGS[normalizedId] || null;
};

/**
 * Check if a promotion is a Trade-Up promotion
 */
export const isTradeUpPromotion = (promotionId: string): boolean => {
	const config = getPromotionConfig(promotionId);
	return config?.category === 'TRADE_UP' || false;
};

/**
 * Count Trade-Up and non-Trade-Up promotions in the current selection
 */
export const countPromotionsByCategory = (
	appliedPromotions: Promotion[]
): { tradeUpCount: number; otherCount: number } => {
	return appliedPromotions.reduce(
		(acc, promo) => {
			if (isTradeUpPromotion(promo.id)) {
				acc.tradeUpCount++;
			} else {
				acc.otherCount++;
			}
			return acc;
		},
		{ tradeUpCount: 0, otherCount: 0 }
	);
};

// ============================================================================
// PROMOTION VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validates if a promotion can be added based on current selection
 *
 * Business Rules:
 * - 1 non-Trade-Up promotion: ✅ Allowed
 * - 2 non-Trade-Up promotions: ❌ Not allowed
 * - 1 Trade-Up only: ✅ Allowed
 * - 1 Trade-Up + 1 other: ✅ Allowed
 * - 2 Trade-Up promotions: ❌ Not allowed
 * - Trade-Up + 2 others: ❌ Not allowed (implied by "2 non-Trade-Up" rule)
 */
export const canPromotionBeAdded = (
	promotionToAdd: string,
	appliedPromotions: Promotion[]
): PromotionValidationResult => {
	const { tradeUpCount, otherCount } = countPromotionsByCategory(appliedPromotions);
	const isTradeUpToAdd = isTradeUpPromotion(promotionToAdd);
	const isAlreadyApplied = appliedPromotions.some((p) => p.id === promotionToAdd);

	// Check if promotion is already applied
	if (isAlreadyApplied) {
		return {
			canAdd: false,
			reason: 'This promotion is already applied',
			translationKey: 'promotions.errors.alreadyApplied',
		};
	}

	// Check if adding would result in 2 Trade-Up promotions
	if (isTradeUpToAdd && tradeUpCount >= 1) {
		return {
			canAdd: false,
			reason: 'Only one Trade-Up promotion allowed',
			translationKey: 'promotions.errors.onlyOneTradeUp',
		};
	}

	// Check if adding non-Trade-Up would exceed the 1 non-Trade-Up limit
	if (!isTradeUpToAdd && otherCount >= 1) {
		return {
			canAdd: false,
			reason: 'Only one non-Trade-Up promotion allowed',
			translationKey: 'promotions.errors.onlyOneNonTradeUp',
		};
	}

	return { canAdd: true };
};
